<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">受眾規則管理</h1>
      </div>
      <div class="header-actions">
        <button
          type="button"
          class="btn btn-primary"
          @click="handleCreate"
          :disabled="false"
          :title="'新增規則'"
        >
          <i class="ri-add-line"></i>
          新增規則
        </button>
      </div>
    </div>

    <div class="page-container">
    <div v-if="ruleLimitInfo" class="limit-info limit-info-banner" :class="{'limit-warning': !ruleLimitInfo.canCreateDynamic}">
      <span class="limit-text">
        動態更新規則：
        <span class="remaining">{{ ruleLimitInfo.currentDynamicCount }} / {{ ruleLimitInfo.maxDynamicRules }}</span>
        （靜態規則不限量）
      </span>
    </div>

    <!-- 受眾規則說明 -->
    <div class="list-page-intro" :class="{ 'is-expanded': introOpen }">
      <button type="button" class="intro-toggle" @click="introOpen = !introOpen">
        <span class="intro-title">
          <i class="ri-information-line"></i>
          受眾規則的作用
        </span>
        <span class="intro-toggle-action">
          <span class="intro-toggle-label">{{ introOpen ? '收起' : '查看說明' }}</span>
          <i :class="introOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
        </span>
      </button>
      <div v-show="introOpen" class="intro-body">
        <p class="intro-text">受眾規則用來定義「符合條件的會員」群組，可依標籤、消費、行為等條件篩選出目標對象。</p>
        <ul class="intro-list">
          <li><strong>用途</strong>：用於 LINE 推播、行銷活動、優惠發放等，選擇要觸及的會員群。</li>
          <li><strong>動態規則</strong>：會依設定頻率自動更新，受眾人數隨會員狀態即時變動。</li>
          <li><strong>靜態規則</strong>：建立當下的快照，受眾人數固定，適合一次性活動。</li>
        </ul>
      </div>
    </div>

    <!-- 搜尋表單 -->
    <div class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchForm.name"
          type="text"
          class="form-control"
          placeholder="搜尋規則名稱..."
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
      <div class="filter-group">
        <select v-model="searchForm.isActive" class="form-control" @change="handleSearch">
          <option value="">全部啟用狀態</option>
          <option :value="true">啟用</option>
          <option :value="false">停用</option>
        </select>
      </div>
      <div class="filter-group">
        <select v-model="searchForm.isDynamic" class="form-control" @change="handleSearch">
          <option value="">全部類型</option>
          <option :value="true">動態更新</option>
          <option :value="false">靜態規則</option>
        </select>
      </div>
      <button class="btn btn-secondary" @click="handleReset">
        <i class="ri-refresh-line"></i>
        重置
      </button>
    </div>

    <!-- 載入狀態 -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <!-- 規則列表 -->
    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>規則名稱</th>
            <th>規則說明</th>
            <th>組合邏輯</th>
            <th>受眾人數</th>
            <th>動態更新</th>
            <th>更新頻率</th>
            <th>啟用</th>
            <th>最後計算</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="rule in rules" :key="rule.id">
            <td>{{ rule.name }}</td>
            <td>{{ rule.description || '-' }}</td>
            <td class="text-center">
              <span class="badge" :class="rule.logic === 'AND' ? 'badge-info' : 'badge-warning'">
                {{ rule.logic }}
              </span>
            </td>
            <td class="text-center">{{ rule.audienceCount || 0 }}</td>
            <td class="text-center">
              <span v-if="rule.isDynamic" class="badge badge-success">是</span>
              <span v-else class="badge badge-secondary">否</span>
            </td>
            <td class="text-center">
              <span v-if="rule.updateFrequency" class="badge badge-info">
                {{ getUpdateFrequencyLabel(rule.updateFrequency) }}
              </span>
              <span v-else>-</span>
            </td>
            <td class="text-center">
              <div class="active-status-cell">
                <span
                  class="badge"
                  :class="rule.isActive ? 'badge-success' : 'badge-secondary'"
                >
                  {{ rule.isActive ? '啟用' : '停用' }}
                </span>
                <label
                  class="switch"
                  :title="rule.isActive ? '點擊停用：規則將不會出現在其他功能選單，動態規則也不會自動更新' : '點擊啟用：規則可供選用並依設定自動更新'"
                >
                  <input type="checkbox" v-model="rule.isActive" @change="handleToggleActive(rule)" />
                  <span class="slider"></span>
                </label>
              </div>
            </td>
            <td>
              <div v-if="rule.lastCalculatedAt">
                {{ formatDate(rule.lastCalculatedAt) }}
              </div>
              <span v-else>尚未計算</span>
            </td>
            <td>
              <div class="action-buttons">
                <button class="btn btn-sm btn-success" @click="handleUpdateAudience(rule)" title="更新受眾">
                  <i class="ri-refresh-line"></i>
                  更新
                </button>
                <button class="btn btn-sm btn-info" @click="handlePreview(rule)" title="預覽受眾">
                  <i class="ri-eye-line"></i>
                  預覽
                </button>
                <button class="btn btn-sm btn-primary" @click="handleEdit(rule)" title="編輯">
                  <i class="ri-edit-line"></i>
                  編輯
                </button>
                <button class="btn btn-sm btn-danger" @click="handleDelete(rule)" title="刪除">
                  <i class="ri-delete-bin-line"></i>
                  刪除
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="rules.length === 0" class="empty-state">
        <p>暫無規則</p>
      </div>

      <!-- 分頁 -->
      <ListPagination
        v-if="pagination.totalPages > 1"
        :page="pagination.page"
        :total-pages="pagination.totalPages"
        :total="pagination.total"
        @change="handlePageChange"
      />
    </div>

    <!-- 預覽彈窗 -->
    <div v-if="showPreviewModal" class="modal-overlay" @click.self="closePreviewModal">
      <div class="modal-dialog">
        <div class="modal-header">
          <h3>受眾預覽</h3>
          <button class="btn-close" @click="closePreviewModal">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="previewLoading" class="loading-state">
            <div class="spinner"></div>
            <p>計算中...</p>
          </div>
          <div v-else-if="previewData">
            <div class="preview-info">
              <p><strong>預估受眾人數：</strong>{{ previewData.estimatedCount }} 人</p>
              <p><strong>計算時間：</strong>{{ formatDate(previewData.calculatedAt) }}</p>
              <p><strong>執行耗時：</strong>{{ previewData.executionTime }} ms</p>
            </div>
            <div v-if="previewData.breakdown" class="breakdown-section">
              <h4>受眾分布</h4>
              <div v-if="previewData.breakdown.byTier && Object.keys(previewData.breakdown.byTier).length > 0" class="breakdown-item">
                <strong>會員等級：</strong>
                <div class="breakdown-list">
                  <span v-for="(count, tier) in previewData.breakdown.byTier" :key="tier" class="badge badge-info">
                    {{ tier }}: {{ count }}
                  </span>
                </div>
              </div>
              <div v-if="previewData.breakdown.byGender && Object.keys(previewData.breakdown.byGender).length > 0" class="breakdown-item">
                <strong>性別：</strong>
                <div class="breakdown-list">
                  <span v-for="(count, gender) in previewData.breakdown.byGender" :key="gender" class="badge badge-info">
                    {{ gender === 'male' ? '男性' : gender === 'female' ? '女性' : gender }}: {{ count }}
                  </span>
                </div>
              </div>
              <div v-if="previewData.breakdown.byCity && Object.keys(previewData.breakdown.byCity).length > 0" class="breakdown-item">
                <strong>城市：</strong>
                <div class="breakdown-list">
                  <span v-for="(count, city) in previewData.breakdown.byCity" :key="city" class="badge badge-info">
                    {{ city }}: {{ count }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { audienceRuleService } from '@/api/modules/audienceRules';
import { showSuccess, showError, showConfirm } from '@/utils/swal.js';

export default {
  name: 'AudienceRulesList',
  setup() {
    const router = useRouter();
    const loading = ref(false);
    const introOpen = ref(false);
    const rules = ref([]);
    const ruleLimitInfo = ref(null);
    const pagination = ref({
      page: 1,
      pageSize: 20,
      total: 0,
      totalPages: 0,
      hasNext: false,
      hasPrev: false
    });

    const searchForm = reactive({
      name: '',
      isActive: '',
      isDynamic: ''
    });

    // 數量限制資訊
    const canCreateDynamicRule = computed(() => {
      return ruleLimitInfo.value ? ruleLimitInfo.value.canCreateDynamic : true;
    });

    // 格式化日期
    const formatDate = (date) => {
      if (!date) return '-';
      return new Date(date).toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    };

    // 取得更新頻率標籤
    const getUpdateFrequencyLabel = (frequency) => {
      const labels = {
        realtime: '即時',
        hourly: '每小時',
        daily: '每日',
        manual: '手動'
      };
      return labels[frequency] || frequency;
    };

    // 載入規則列表
    const loadRules = async () => {
      loading.value = true;
      try {
        const params = {
          page: pagination.value.page,
          pageSize: pagination.value.pageSize
        };
        if (searchForm.name) params.name = searchForm.name;
        if (searchForm.isActive !== '') params.isActive = searchForm.isActive;
        if (searchForm.isDynamic !== '') params.isDynamic = searchForm.isDynamic;

        const response = await audienceRuleService.getAudienceRules(params);
        rules.value = response.data.items || [];
        const pg = response.data?.pagination;
        if (pg) {
          pagination.value = {
            ...pagination.value,
            ...pg,
            total: pg.total ?? pg.totalItems ?? 0
          };
        }
        ruleLimitInfo.value = response.data.limitInfo || null;
      } catch (error) {
        console.error('載入規則列表失敗:', error);
        await showError('載入失敗', error.message || '載入規則列表失敗');
      } finally {
        loading.value = false;
      }
    };

    // 搜尋
    const handleSearch = () => {
      pagination.value.page = 1;
      loadRules();
    };

    // 重置
    const handleReset = () => {
      searchForm.name = '';
      searchForm.isActive = '';
      searchForm.isDynamic = '';
      pagination.value.page = 1;
      loadRules();
    };

    // 分頁
    const handlePageChange = (page) => {
      pagination.value.page = page;
      loadRules();
    };

    // 新增規則
    const handleCreate = () => {
      router.push('/client/audience-rules/create');
    };

    // 編輯規則
    const handleEdit = (rule) => {
      router.push(`/client/audience-rules/edit/${rule.id}`);
    };

    // 預覽受眾
    const showPreviewModal = ref(false);
    const previewLoading = ref(false);
    const previewData = ref(null);
    const previewRule = ref(null);

    const handlePreview = async (rule) => {
      previewRule.value = rule;
      showPreviewModal.value = true;
      previewLoading.value = true;
      previewData.value = null;

      try {
        const response = await audienceRuleService.previewAudience(rule.rulesConfig);
        previewData.value = response.data;
      } catch (error) {
        console.error('預覽受眾失敗:', error);
        await showError('預覽失敗', error.message || '預覽受眾失敗');
      } finally {
        previewLoading.value = false;
      }
    };

    const closePreviewModal = () => {
      showPreviewModal.value = false;
      previewRule.value = null;
      previewData.value = null;
    };

    // 啟用/停用規則
    const handleToggleActive = async (rule) => {
      try {
        await audienceRuleService.toggleAudienceRule(rule.id, rule.isActive);
        await showSuccess('更新成功', rule.isActive ? '規則已啟用' : '規則已停用', 1500);
        await loadRules();
      } catch (error) {
        console.error('更新規則狀態失敗:', error);
        await showError('更新失敗', error.message || '更新規則狀態失敗');
        rule.isActive = !rule.isActive; // 恢復原狀態
      }
    };

    // 刪除規則
    const handleDelete = async (rule) => {
      try {
        const confirmed = await showConfirm(
          '確認刪除',
          `確定要刪除規則「${rule.name}」嗎？`,
          '刪除',
          '取消',
          'warning'
        );

        if (!confirmed) return;

        loading.value = true;
        await audienceRuleService.deleteAudienceRule(rule.id);
        await showSuccess('刪除成功', '');
        await loadRules();
      } catch (error) {
        console.error('刪除規則失敗:', error);
        await showError('刪除失敗', error.message || '刪除規則失敗');
      } finally {
        loading.value = false;
      }
    };

    // 手動更新受眾
    const handleUpdateAudience = async (rule) => {
      try {
        loading.value = true;
        const response = await audienceRuleService.calculateAudience(rule.id);
        if (response.data?.queued) {
          await showSuccess('已開始更新', '人數將在背景計算，完成後重新整理列表即可看到', 2500);
        } else {
          await showSuccess('更新成功', `受眾人數：${response.data.audienceCount} 人`, 2000);
          await loadRules();
        }
      } catch (error) {
        console.error('更新受眾失敗:', error);
        await showError('更新失敗', error.message || '更新受眾失敗');
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      loadRules();
    });

    return {
      loading,
      introOpen,
      rules,
      ruleLimitInfo,
      canCreateDynamicRule,
      pagination,
      searchForm,
      formatDate,
      getUpdateFrequencyLabel,
      loadRules,
      handleSearch,
      handleReset,
      handlePageChange,
      handleCreate,
      handleEdit,
      handlePreview,
      showPreviewModal,
      previewLoading,
      previewData,
      closePreviewModal,
      handleToggleActive,
      handleDelete,
      handleUpdateAudience
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.limit-info-banner {
  margin-bottom: 16px;
}

.table-wrapper table {
  min-width: 1200px;
}

.action-buttons {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.text-center {
  text-align: center;
}

.active-status-cell {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.active-status-cell .badge {
  min-width: 44px;
}

/* Switch 樣式 */
.switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .4s;
  border-radius: 24px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: .4s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: #667eea;
}

input:checked + .slider:before {
  transform: translateX(20px);
}

/* 彈窗樣式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-dialog {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.btn-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.btn-close:hover {
  background: #f5f5f5;
  color: #333;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.preview-info {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 6px;
  margin-bottom: 20px;
}

.preview-info p {
  margin: 8px 0;
  font-size: 14px;
  color: #555;
}

.preview-info strong {
  color: #333;
  margin-right: 8px;
}

.breakdown-section {
  margin-top: 20px;
}

.breakdown-section h4 {
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.breakdown-item {
  margin-bottom: 16px;
}

.breakdown-item strong {
  display: block;
  margin-bottom: 8px;
  color: #555;
  font-size: 14px;
}

.breakdown-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.page-info {
  font-size: 14px;
  color: #666;
}
</style>
